import { apiDelete, apiGet, apiPatch, apiPost } from 'core/api/client';
import { asPaginated } from 'core/api/unwrap';
import { endpoints } from 'core/api/endpoints';
import { ApiError } from 'core/errors/ApiError';
import { normalizeAccessCode } from 'features/assessment/accessCodeUtils';
import {
  aggregateEntitlementCredits,
  buildDashboard,
  findMembership,
  mapAccessCode,
  mapParticipant,
  mapPurchase,
  mapSponsoredResult,
} from 'features/company/companyMappers';

function mapCodeList(response) {
  const paginated = asPaginated(response);
  return {
    ...paginated,
    items: (paginated.items || []).map(mapAccessCode),
  };
}

function mapParticipantList(response) {
  const paginated = asPaginated(response);
  return {
    ...paginated,
    items: (paginated.items || []).map(mapParticipant),
  };
}

export const companyApi = {
  plans(params, config) {
    return apiGet(endpoints.companyAssessmentPlans, { params, ...config }).then(asPaginated);
  },

  async planFromCatalog(planId, config) {
    const list = await this.plans({ page: 1, per_page: 50 }, config);
    const plan = (list.items || []).find((entry) => String(entry.id) === String(planId));
    if (!plan) {
      throw new ApiError({ status: 404, message: 'Plan not found.' });
    }
    return { plan };
  },

  companies(params, config) {
    return apiGet(endpoints.companies, { params, ...config }).then(asPaginated);
  },

  async resolveSession(userId, config) {
    const list = await this.companies({ page: 1, per_page: 1 }, config);
    const company = list.items?.[0];
    if (!company) {
      throw new ApiError({ status: 404, message: 'Company not found.' });
    }
    const membership = findMembership(company, userId);
    if (!membership) {
      throw new ApiError({ status: 403, message: 'Forbidden.' });
    }
    const [entitlements, participants, codes] = await Promise.all([
      this.entitlements(company.id, { page: 1, per_page: 50, status: 'active' }, config),
      this.participants(company.id, { page: 1, per_page: 5 }, config),
      this.codes(company.id, { page: 1, per_page: 1, status: 'active' }, config),
    ]);
    return {
      company,
      membership,
      dashboard: buildDashboard({ entitlements, participants, codes }),
    };
  },

  createCompany(body, config) {
    return apiPost(endpoints.companies, body, config);
  },

  updateCompany(companyId, body, config) {
    return apiPatch(endpoints.company(companyId), body, config);
  },

  members(companyId, params, config) {
    return apiGet(endpoints.companyMemberships(companyId), { params, ...config }).then(asPaginated);
  },

  addMember(companyId, body, config) {
    return apiPost(endpoints.companyMemberships(companyId), body, config);
  },

  updateMember(companyId, membershipId, body, config) {
    return apiPatch(endpoints.companyMembership(companyId, membershipId), body, config);
  },

  removeMember(companyId, membershipId, config) {
    return apiDelete(endpoints.companyMembership(companyId, membershipId), config);
  },

  async startPurchase(companyId, planId, idempotencyKey, config) {
    const created = await apiPost(endpoints.companyPurchases(companyId), {
      plan_id: Number(planId),
      idempotency_key: idempotencyKey,
    }, config);
    const purchase = created?.purchase || created;
    const checkout = await apiPost(endpoints.companyPurchaseCheckout(companyId, purchase.id), {}, config);
    const checkoutPurchase = checkout?.purchase || purchase;
    const payment = checkout?.payment || {};
    return {
      purchase: mapPurchase(checkoutPurchase, payment),
      payment,
    };
  },

  purchases(companyId, params, config) {
    return apiGet(endpoints.companyPurchases(companyId), { params, ...config }).then(asPaginated);
  },

  async purchase(companyId, purchaseId, config) {
    const response = await apiGet(endpoints.companyPurchase(companyId, purchaseId), config);
    const purchase = response?.purchase || response;
    return { purchase: mapPurchase(purchase) };
  },

  async creditsSummary(companyId, config) {
    const entitlements = await this.entitlements(companyId, { page: 1, per_page: 50 }, config);
    return { credits: aggregateEntitlementCredits(entitlements.items || []) };
  },

  entitlements(companyId, params, config) {
    return apiGet(endpoints.companyEntitlements(companyId), { params, ...config }).then(asPaginated);
  },

  codes(companyId, params, config) {
    return apiGet(endpoints.companyCodes(companyId), { params, ...config }).then(mapCodeList);
  },

  async generateCodes(companyId, body, config) {
    const entitlementId = body.entitlement_id;
    const quantity = Number(body.quantity || 1);
    const payload = { label: body.label || undefined };
    const response = quantity <= 1
      ? await apiPost(endpoints.companyEntitlementCodes(companyId, entitlementId), payload, config)
      : await apiPost(endpoints.companyEntitlementCodeBatches(companyId, entitlementId), {
        ...payload,
        quantity,
      }, config);
    return {
      ...response,
      codes: (response?.codes || []).map(mapAccessCode),
    };
  },

  revokeCode(companyId, codeId, config) {
    return apiPost(endpoints.companyCodeRevoke(companyId, codeId), {}, config);
  },

  participants(companyId, params, config) {
    return apiGet(endpoints.companySponsoredParticipants(companyId), { params, ...config }).then(mapParticipantList);
  },

  async participantResult(companyId, attemptId, config) {
    const response = await apiGet(endpoints.companySponsoredParticipant(companyId, attemptId), config);
    return {
      participant: response,
      result: mapSponsoredResult(response?.result || response),
    };
  },

  inspectAccessCode(body, config) {
    return apiPost(endpoints.assessmentAccessCodeInspect, {
      code: normalizeAccessCode(body.code),
    }, config);
  },

  redeemAccessCode(body, config) {
    return apiPost(endpoints.assessmentAccessCodeRedeem, {
      code: normalizeAccessCode(body.code),
      share_result_with_company: body.share_result_with_company ?? true,
    }, config);
  },
};
